/**
 * #1023 / #1227 — the org's Polymarket US holdings and trades, read through
 * the org-scoped manager routes and narrowed by the page's owner and sport
 * filters. The page reads once and every US row on it comes from here.
 *
 * Same authority as Trader Detail: the manager/admin role in the org, through
 * the org-scoped manager routes; each member's rows are what Trader Detail
 * reads for that member.
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { sportMatchesFilter, type SportFilter } from '@/lib/kalshiTicker/sportFilter';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import { validUsFillFee } from '@/lib/polymarketUs/fillsClient';
import type { UsMarketClient } from '@/lib/polymarketUs/marketClient';
import type { UsPositionRow } from '@/lib/polymarketUs/types';
import { ownerMatchesFilter, resolveOwnerAttribution, type OwnerAttribution, type OwnerFilterKey } from '@/lib/ownerAttribution';
import {
  readUsManagerAccountInventory, readJson, traderPositions, usManagerScopeKey, US_MANAGER_API as API,
  type ManagerUsFill, type UsAccountCheck, type UsInstitutionalDeskAccount,
} from '@/views/TraderDetail/usManagerReads';
import { UsOrgSportLookup } from './usOrgSports';
import { UsMarketServing } from '@/lib/polymarketUs/marketServing';
import { usMarketClass } from '@/lib/polymarketUs/marketFamily';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { useSharedNow } from '@/lib/infra/sharedClock';
import type { TraderPositions } from '@/views/TraderDetail/usManagerReads';
import { useUsPositionMarks, type UsPositionMarks } from '@/hooks/useUsPositionMarks';

/** `member: false` (#1055): the owner has left the org (deleted or moved), but
 *  their holdings are still on the org's accounts. The relay leaves them unnamed. */
type Member = { userId: string; username: string | null; member?: boolean };
export type OrgUsPosition = { row: UsPositionRow; accountId: string; owner: OwnerAttribution; former: boolean; title: string | null };
export type OrgUsFill = ManagerUsFill & { accountId: string; owners: OwnerAttribution[] };
export type UsOrgLoaded = {
  key: string;
  epoch: number;
  /** The page refresh this answer is for; a newer refreshKey means a re-read is in flight. */
  refresh: number;
  accounts: UsAccountCheck;
  institutionalAccounts?: UsInstitutionalDeskAccount[];
  positions: { rows: OrgUsPosition[]; complete: boolean; scopeComplete: boolean;
    members: ReadonlyMap<string, TraderPositions> } | null;
  fills: { rows: OrgUsFill[]; complete: boolean; unlistedFormerMembers: number } | null;
};

export const FORMER_TITLE = "No longer in this org. These contracts are still on the org's Polymarket US account.";
const ownerOf = (m: Member) => resolveOwnerAttribution({ ownerKind: 'trader', ownerUserId: m.userId,
  ownerUsername: m.member === false ? `former member ${m.userId.slice(0, 8)}` : m.username });

/** Each member's accounts through Trader Detail's own mapping; one bad member refuses the read. */
function orgPositions(body: unknown): UsOrgLoaded['positions'] {
  const b = body as { members?: unknown; complete?: unknown } | null;
  if (!Array.isArray(b?.members)) return null;
  const rows: OrgUsPosition[] = [];
  const members = new Map<string, TraderPositions>();
  let complete = b.complete === true;
  for (const m of b.members as (Member & { accounts?: unknown; complete?: unknown })[]) {
    if (typeof m?.userId !== 'string' || !m.userId) return null;
    if (members.has(m.userId)) return null;
    const read = traderPositions(m, m.userId);
    if (!read) return null;
    members.set(m.userId, read);
    if (!read.complete) complete = false;
    const owner = ownerOf(m);
    const former = m.member === false;
    const accountOf = new Map((m.accounts as { accountId: string }[]).map((a) => [usManagerScopeKey(m.userId, a.accountId), a.accountId]));
    rows.push(...read.rows.map((row) => ({ row, accountId: accountOf.get(row.scope.scopeKey) ?? '', owner, former,
      title: read.titles.get(row.target.marketSlug) ?? null })));
  }
  return { rows, complete, scopeComplete: b.complete === true, members };
}

function orgFills(body: unknown): UsOrgLoaded['fills'] {
  const b = body as { fills?: unknown; complete?: unknown; unlistedFormerMembers?: unknown } | null;
  if (!Array.isArray(b?.fills)) return null;
  const rows: OrgUsFill[] = [];
  for (const f of b.fills as (ManagerUsFill & { accountId?: unknown; owners?: unknown })[]) {
    if (typeof f?.executionId !== 'string' || typeof f.accountId !== 'string' || !Array.isArray(f.owners)
      || f.owners.length === 0 || !Array.isArray(f.disputed) || !validUsFillFee(f)) return null;
    rows.push({ ...f, accountId: f.accountId, owners: (f.owners as Member[]).map(ownerOf) });
  }
  const unlisted = b.unlistedFormerMembers;
  return { rows, complete: b.complete === true,
    unlistedFormerMembers: typeof unlisted === 'number' && Number.isSafeInteger(unlisted) && unlisted > 0 ? unlisted : 0 };
}

/** #1202: the page's sport chips on US rows, the US convention (the Tracker's,
 *  usMarketClassifier.ts): a market whose sport the catalogue does not give is
 *  visible under every chip and counted as unclassified, never guessed. Runs
 *  only on rows the owner filter already passed, so `hidden` counts only what
 *  the sport hid. */
function bySport<R>(rows: readonly R[], slugOf: (row: R) => string | null, sport: (slug: string) => string | null, filter: SportFilter) {
  if (filter.size === 0) return { rows, hidden: 0, pending: 0 };
  let hidden = 0;
  let pending = 0;
  const shown = rows.filter((row) => {
    const slug = slugOf(row);
    const s = slug === null ? null : sport(slug);
    if (s === null) { pending++; return true; }
    if (!sportMatchesFilter(s as Parameters<typeof sportMatchesFilter>[0], filter)) { hidden++; return false; }
    return true;
  });
  return { rows: shown, hidden, pending };
}

export type UsOrgRowsInput = {
  orgQueryParam: string;
  ownerFilter: OwnerFilterKey;
  /** The page's sport chips. Empty = no chip: no catalogue read, rows as before. */
  sportFilter: SportFilter;
  /** The catalogue client; tests inject one. Default: one account-free browser client per mount. */
  marketClient?: Pick<UsMarketClient, 'listPublicMarkets'>;
  /** Bumped by the page's Refresh button; the shown rows stay until the new read lands. */
  refreshKey?: number;
  /** Read the current holdings' exact catalogue books as well as sport labels. */
  economics?: boolean;
  /** The member table has no fills list and does not need that read. */
  includeFills?: boolean;
};

export type UsOrgRows = {
  /** The answer for this org, or null while its first read is out. */
  current: UsOrgLoaded | null;
  positions: { rows: readonly OrgUsPosition[]; hidden: number; pending: number };
  fills: { rows: readonly OrgUsFill[]; hidden: number; pending: number };
  /** A re-read (the page Refresh, or Retry) is in flight until its answer lands. */
  reReading: boolean;
  markets: UsMarketServing;
  marks: UsPositionMarks;
  marketVersion: number;
  nowMs: number;
};

const subscribeAuth = (notify: () => void) => useAuthStore.subscribe(notify);

export function useUsOrgRows({ orgQueryParam, ownerFilter, sportFilter, marketClient, refreshKey = 0,
  economics = false, includeFills = true }: UsOrgRowsInput): UsOrgRows {
  const [loaded, setLoaded] = useState<UsOrgLoaded | null>(null);
  const epoch = useSyncExternalStore(subscribeAuth, getAuthSessionEpoch);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { state: accounts, institutionalAccounts } = await readUsManagerAccountInventory(orgQueryParam);
      if (cancelled || getAuthSessionEpoch() !== epoch) return;
      if (accounts !== 'present') {
        if (!cancelled && getAuthSessionEpoch() === epoch) setLoaded({ key: orgQueryParam, epoch, refresh: refreshKey, accounts, institutionalAccounts, positions: null, fills: null });
        return;
      }
      const [positions, fills] = await Promise.all([
        readJson(`${API}/org/positions${orgQueryParam}`),
        includeFills ? readJson(`${API}/org/fills${orgQueryParam}`) : Promise.resolve(null),
      ]);
      if (!cancelled && getAuthSessionEpoch() === epoch) setLoaded({ key: orgQueryParam, epoch, refresh: refreshKey, accounts, institutionalAccounts, positions: orgPositions(positions), fills: orgFills(fills) });
    })();
    return () => { cancelled = true; };
  }, [orgQueryParam, refreshKey, epoch, includeFills]);

  const current = loaded?.key === orgQueryParam && loaded.epoch === epoch ? loaded : null;

  // One public catalogue reader per inspected org/session. Current held slugs
  // and the bounded recent-fills list share its 20-slug pages. A new inventory
  // snapshot/Refresh asks once; owner/sport changes and local expiry never poll.
  const markets = useMemo(() => {
    void orgQueryParam; void epoch;
    const client = marketClient ?? createUsBrowserMarketClient();
    return new UsMarketServing({ listPublicMarkets: client.listPublicMarkets.bind(client),
      invalidate: () => { if ('invalidate' in client && typeof client.invalidate === 'function') client.invalidate(); } });
  }, [orgQueryParam, epoch, marketClient]);
  useEffect(() => { markets.open(); return () => markets.close(); }, [markets]);
  useEffect(() => {
    markets.request(economics ? [
      ...(current?.positions?.rows ?? []).map(p => p.row.target.marketSlug),
      ...(current?.fills?.rows ?? []).flatMap(f => f.marketSlug === null ? [] : [f.marketSlug]),
    ] : []);
  }, [markets, current, economics]);
  const marketVersion = useSyncExternalStore(markets.subscribe, markets.getVersion);
  const heldSlugs = useMemo(() => economics ? (current?.positions?.rows ?? []).map(p => p.row.target.marketSlug) : [], [current, economics]);
  const marks = useUsPositionMarks(includeFills ? 'org-detail-us' : 'org-traders-us', heldSlugs, markets, marketVersion);
  const nowMs = useSharedNow(1000, economics && (current?.positions?.rows.length ?? 0) > 0);

  // #1202: the markets' sports, read only while a chip is on.
  const [lookup] = useState(() => new UsOrgSportLookup(marketClient ?? createUsBrowserMarketClient()));
  useEffect(() => {
    lookup.open();
    return () => lookup.close();
  }, [lookup]);
  const chipOn = sportFilter.size > 0;
  // A Refresh with a chip on asks the catalogue again for the markets it did
  // not hold; the re-read's rows then request them once.
  const seenRefresh = useRef(refreshKey);
  useEffect(() => {
    if (seenRefresh.current === refreshKey) return;
    seenRefresh.current = refreshKey;
    if (chipOn && !economics) lookup.retryMisses();
  }, [lookup, refreshKey, chipOn, economics]);
  useEffect(() => {
    const slugs = new Set<string>();
    if (chipOn && !economics) {
      for (const p of current?.positions?.rows ?? []) slugs.add(p.row.target.marketSlug);
      for (const f of current?.fills?.rows ?? []) if (f.marketSlug !== null) slugs.add(f.marketSlug);
    }
    // No chip: an empty request, which drops any queued read.
    lookup.request(slugs);
  }, [lookup, current, chipOn, economics]);
  const sportsVersion = useSyncExternalStore(lookup.subscribe, lookup.getVersion);

  const positions = useMemo(() => {
    void sportsVersion;
    void marketVersion;
    const owned = current?.positions?.rows.filter((p) => ownerMatchesFilter(p.owner, ownerFilter)) ?? [];
    return bySport(owned, (p) => p.row.target.marketSlug, (slug) => {
      const market = markets.getMarket(slug);
      return economics ? market ? usMarketClass(market).sport : null : lookup.get(slug)?.sport ?? null;
    }, sportFilter);
  }, [current, ownerFilter, sportFilter, lookup, sportsVersion, economics, markets, marketVersion]);
  const fills = useMemo(() => {
    void sportsVersion;
    void marketVersion;
    const owned = current?.fills?.rows.filter((f) => f.owners.some((o) => ownerMatchesFilter(o, ownerFilter))) ?? [];
    // A disputed fill's market is unknown, so it stays visible and counted as unclassified.
    return bySport(owned, (f) => f.marketSlug, (slug) => {
      const market = markets.getMarket(slug);
      return economics ? market ? usMarketClass(market).sport : null : lookup.get(slug)?.sport ?? null;
    }, sportFilter);
  }, [current, ownerFilter, sportFilter, lookup, sportsVersion, economics, markets, marketVersion]);

  return {
    current,
    positions,
    fills,
    reReading: current?.refresh !== refreshKey,
    markets, marks, marketVersion, nowMs,
  };
}
